import { toast } from '@/components/toast';
import { GET_TRANSLATION_GROUPS_KEY, useGetTranslationGroup } from '@/state/translations';
import { AppRoutes } from '@/utils/types';
import { TranslationStatus } from '@d-id/types';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { isGroupDraftEditable, isGroupReadonly } from '../utils';
import { useCurrentDraftGroupId } from './hooks/useCurrentDraftGroupId';
import TranslationDraftEditorPage from './TranslationDraftEditorPage';
import TranslationDraftLoading from './TranslationDraftLoading';

const ProofreadPageContainer = styled.div`
    background-color: white;
    display: flex;
    flex-direction: column;
    height: 100vh;
    max-width: 100vw;
`;

const refetchInterval = 5000;

function TranslationDraftPage() {
    const groupId = useCurrentDraftGroupId();

    const [groupRefetchInterval, setGroupRefetchInterval] = useState<number | false>(false);
    const { data: group, isError: groupError } = useGetTranslationGroup(groupId, { refetchInterval: groupRefetchInterval });
    const draftEditable = group && isGroupDraftEditable(group);

    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const translations = group?.translations;

    const translationsFailed = translations?.some(({ status }) => status === TranslationStatus.Error);

    useEffect(() => {
        if (group && isGroupReadonly(group)) {
            navigate(AppRoutes.Translation);
        }
    }, [group]);

    useEffect(() => {
        if (translationsFailed) {
            toast.error({ title: 'Translations failed to generate' });
            navigate(AppRoutes.Translation);
        }
    }, [translationsFailed]);

    useEffect(() => {
        setGroupRefetchInterval(draftEditable ? false : refetchInterval);
        queryClient.invalidateQueries(GET_TRANSLATION_GROUPS_KEY);
    }, [draftEditable]);

    useEffect(() => {
        if (groupError) {
            toast.error({ title: 'Failed to load translations' });
            setGroupRefetchInterval(false);
        }
    }, [groupError]);

    const showLoading = !(translations && group) || !draftEditable;

    return (
        <ProofreadPageContainer>
            {showLoading ? <TranslationDraftLoading /> : <TranslationDraftEditorPage translations={translations} />}
        </ProofreadPageContainer>
    );
}

export default TranslationDraftPage;
